import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Form as RsuiteForm,
    Heading,
    HStack,
    Message,
    Text,
} from "rsuite";
import { FormProps } from "rsuite/esm/Form/Form";

import i18n from "utils/i18n";

import { SecretExpiration } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import { StudioWorkflow } from "store/workflows/workflows-types";

import Copy from "components/Common/Copy/Copy";
import ProgressButton from "components/Workflows/Button/Button";

import { getSecretExpirationMessage } from "../studio-helpers";

import "./Form.less";

export type ApplicationFormValue = {
    displayName?: string;
    appId?: string;
    passwordEndDateTime?: string | null;
    link?: string | null;
};

interface Props extends FormProps {
    title?: string;
    environmentId?: string;
    tenantId?: string;
    rotationWorkflow?: StudioWorkflow | null;
    rotationDisabled?: boolean;
    secretExpiration?: SecretExpiration | null;
    onRotateClientSecret?: () => void;
    onRotationTerminated?: () => void;
}

const Form: FC<Props> = ({
    title = i18n.t("tenantConfig.studio.applicationForm.header"),
    environmentId = "",
    tenantId = "",
    rotationWorkflow = null,
    rotationDisabled = false,
    secretExpiration = null,
    onRotateClientSecret,
    onRotationTerminated,
    ...props
}: Props) => {
    const { t } = useTranslation();

    const secretExpirationMessage = getSecretExpirationMessage(
        secretExpiration,
        t
    );

    type formValueType = keyof ApplicationFormValue;

    const formFieldKeys: Record<string, formValueType> = {
        displayName: "displayName",
        appId: "appId",
        passwordEndDateTime: "passwordEndDateTime",
    };

    const formValue = props.formValue as ApplicationFormValue;

    return (
        <>
            <RsuiteForm {...props}>
                <Heading style={{ marginBottom: "1rem" }} level={3}>
                    {title}
                </Heading>

                {formValue.link && (
                    <HStack
                        style={{ margin: "1rem 0" }}
                        justifyContent={"flex-start"}
                        spacing={5}
                    >
                        <HStack.Item alignSelf={"center"}>
                            <Text>
                                {t(
                                    "tenantConfig.studio.applicationForm.linkPart1"
                                )}
                            </Text>
                        </HStack.Item>

                        <HStack.Item alignSelf={"center"}>
                            <a
                                className={"studio__form__link"}
                                href={formValue.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t(
                                    "tenantConfig.studio.applicationForm.linkPart2"
                                )}
                            </a>
                        </HStack.Item>
                    </HStack>
                )}

                <RsuiteForm.Group
                    className={"studio__form__group"}
                    style={{ marginTop: "1rem" }}
                >
                    <RsuiteForm.ControlLabel className={"studio__form__label"}>
                        {t(
                            "tenantConfig.studio.applicationForm.displayName.label"
                        )}
                        <RsuiteForm.HelpText
                            className={"studio__form__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.studio.applicationForm.displayName.helpText"
                            )}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>
                    <Copy value={formValue.displayName}>
                        <RsuiteForm.Control
                            className={"studio__form__control"}
                            type="text"
                            readOnly
                            name={formFieldKeys.displayName}
                        />
                    </Copy>
                </RsuiteForm.Group>

                <RsuiteForm.Group className={"studio__form__group"}>
                    <RsuiteForm.ControlLabel className={"studio__form__label"}>
                        {t("tenantConfig.studio.applicationForm.appId.label")}
                        <RsuiteForm.HelpText
                            className={"studio__form__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.studio.applicationForm.appId.helpText"
                            )}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>
                    <Copy value={formValue.appId}>
                        <RsuiteForm.Control
                            className={"studio__form__control"}
                            type="text"
                            readOnly
                            name={formFieldKeys.appId}
                        />
                    </Copy>
                </RsuiteForm.Group>

                {formValue.passwordEndDateTime && (
                    <RsuiteForm.Group className={"studio__form__group"}>
                        <RsuiteForm.ControlLabel
                            className={"studio__form__label"}
                        >
                            {t(
                                "tenantConfig.studio.applicationForm.passwordEndDateTime.label"
                            )}
                            <RsuiteForm.HelpText
                                className={"studio__form__help"}
                                tooltip
                            >
                                {t(
                                    "tenantConfig.studio.applicationForm.passwordEndDateTime.helpText"
                                )}
                            </RsuiteForm.HelpText>
                        </RsuiteForm.ControlLabel>
                        <RsuiteForm.Control
                            className={"studio__form__control"}
                            type="text"
                            readOnly
                            name={formFieldKeys.passwordEndDateTime}
                        />
                    </RsuiteForm.Group>
                )}

                {onRotateClientSecret && (
                    <RsuiteForm.Group
                        className={"studio__form__group--message"}
                    >
                        <RsuiteForm.ControlLabel
                            className={"studio__form__label"}
                        >
                            {t(
                                "tenantConfig.studio.applicationForm.clientSecret.label"
                            )}
                            <RsuiteForm.HelpText
                                className={"studio__form__help"}
                                tooltip
                            >
                                {t(
                                    "tenantConfig.studio.applicationForm.clientSecret.helpText"
                                )}
                            </RsuiteForm.HelpText>
                        </RsuiteForm.ControlLabel>
                        <HStack justifyContent={"flex-start"} spacing={5}>
                            <HStack.Item alignSelf={"center"}>
                                <Button
                                    className={"studio__form__button"}
                                    appearance="ghost"
                                    disabled={rotationDisabled}
                                    onClick={onRotateClientSecret}
                                >
                                    {t(
                                        "tenantConfig.studio.applicationForm.clientSecret.rotateBtn"
                                    )}
                                </Button>
                            </HStack.Item>

                            <HStack.Item alignSelf={"center"}>
                                <ProgressButton
                                    environmentId={environmentId}
                                    tenantId={tenantId}
                                    workflow={rotationWorkflow}
                                    onTerminated={onRotationTerminated}
                                />
                            </HStack.Item>
                        </HStack>

                        {secretExpirationMessage && (
                            <Message
                                className={"studio__form__message"}
                                showIcon
                                type={secretExpirationMessage.type}
                            >
                                {secretExpirationMessage.description}
                            </Message>
                        )}
                    </RsuiteForm.Group>
                )}
            </RsuiteForm>
        </>
    );
};

export default Form;
